import { useState } from "react";

import type {
  AskReferencedEpisodeMetadata,
  AskResponseMetadata,
  AskSourceMetadata,
  AskTemporalMetadata,
} from "@/api/client";
import type { Episode } from "@/hooks/useEpisodes";
import { cn } from "@/lib/utils";

interface AnswerEvidencePanelProps {
  metadataEntries: AskResponseMetadata[];
  fallbackEpisodeNumbers?: number[];
  episodesByNumber: Map<number, Episode>;
  onOpenEpisode: (episode: Episode) => void;
}

function compactLabel(value: string | null | undefined): string {
  if (!value) return "unknown";
  return value.replace(/_/g, " ");
}

function titleCaseLabel(value: string | null | undefined): string {
  return compactLabel(value)
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatTime(ms: number | null | undefined): string | null {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) {
    return null;
  }

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatTimeRange(source: AskSourceMetadata): string | null {
  const start = formatTime(source.start_time_ms);
  const end = formatTime(source.end_time_ms);

  if (start && end) return `${start}-${end}`;
  return start;
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLatency(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "live";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatScore(score: number | null | undefined): string | null {
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  return score.toFixed(2);
}

function formatCharRange(source: AskSourceMetadata): string | null {
  if (
    typeof source.start_char !== "number" ||
    typeof source.end_char !== "number" ||
    source.start_char < 0 ||
    source.end_char <= source.start_char
  ) {
    return null;
  }

  return `${source.start_char.toLocaleString()}-${source.end_char.toLocaleString()}`;
}

function formatTemporalMode(temporal: AskTemporalMetadata): string {
  if (temporal.mode === "latest_episode" && temporal.resolved_episode_count > 1) {
    return `Latest ${temporal.resolved_episode_count} episodes`;
  }

  if (temporal.mode === "latest_episode") {
    return "Latest episode";
  }

  if (temporal.mode === "date_range") {
    const start = formatDate(temporal.start_date);
    const end = formatDate(temporal.end_date);
    if (start && end) return `${start} to ${end}`;
    if (start) return `Since ${start}`;
    if (end) return `Until ${end}`;
  }

  return titleCaseLabel(temporal.mode);
}

function formatTemporalDetail(temporal: AskTemporalMetadata): string {
  const parts = [
    temporal.hard_filter_applied ? "hard filter" : "soft scope",
    temporal.confidence ? `${compactLabel(temporal.confidence)} confidence` : null,
    temporal.source ? compactLabel(temporal.source) : null,
  ].filter(Boolean);

  return parts.join(" · ");
}

function roleTone(role: string): string {
  if (role.includes("claim")) {
    return "bg-green-100 text-green-950 border-green-600 dark:bg-green-900/40 dark:text-green-100 dark:border-green-500";
  }
  if (role.includes("orientation")) {
    return "bg-blue-100 text-blue-950 border-blue-600 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-500";
  }
  if (role.includes("summary")) {
    return "bg-amber-100 text-amber-950 border-amber-500 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-500";
  }
  if (role.includes("transcript") || role.includes("evidence")) {
    return "bg-green-100 text-green-950 border-green-600 dark:bg-green-900/40 dark:text-green-100 dark:border-green-500";
  }
  if (role.includes("candidate")) {
    return "bg-blue-100 text-blue-950 border-blue-600 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-500";
  }
  return "bg-white text-gray-900 border-gray-900 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500";
}

function roleEntries(roles: Record<string, number> | null | undefined): Array<[string, number]> {
  return Object.entries(roles ?? {})
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0].localeCompare(right[0]);
    });
}

function originTone(origin: string): string {
  if (origin.includes("transcript")) {
    return "border-green-600 bg-green-50 text-green-950 dark:border-green-500 dark:bg-green-900/30 dark:text-green-100";
  }
  if (origin.includes("summary")) {
    return "border-amber-600 bg-amber-50 text-amber-950 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-100";
  }
  if (origin.includes("metadata")) {
    return "border-blue-600 bg-blue-50 text-blue-950 dark:border-blue-500 dark:bg-blue-900/30 dark:text-blue-100";
  }
  return "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300";
}

function formatSupportCounts(selected: number, candidates: number): string {
  if (selected === 0 && candidates === 0) return "support not scored";
  return `${selected}/${candidates} selected support`;
}

export default function AnswerEvidencePanel({
  metadataEntries,
  fallbackEpisodeNumbers,
  episodesByNumber,
  onOpenEpisode,
}: AnswerEvidencePanelProps) {
  const [isOpen, setIsOpen] = useState(true);
  const referencedEpisodes = aggregateReferencedEpisodes(
    metadataEntries,
    fallbackEpisodeNumbers,
  );
  const diagnostics = aggregateDiagnostics(metadataEntries);
  const allSources = metadataEntries.flatMap((entry) => entry.sources);
  const sources = allSources.slice(0, 12);
  const sourceCount = allSources.length;
  const queryKinds = Array.from(
    new Set(metadataEntries.map((entry) => compactLabel(entry.query.kind))),
  );
  const temporalScopes = metadataEntries
    .map((entry) => entry.query.temporal)
    .filter((temporal): temporal is AskTemporalMetadata => Boolean(temporal));
  const latestTemporalScope = temporalScopes.at(-1);
  const latestPerformance = metadataEntries.at(-1)?.performance;

  if (metadataEntries.length === 0 && referencedEpisodes.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-xs font-bold transition-transform hover:-translate-y-0.5",
          "border-3 border-gray-900 bg-white text-gray-900 shadow-neubrutalism-sm",
          "dark:border-gray-500 dark:bg-gray-800 dark:text-gray-100 dark:shadow-none",
        )}
        aria-expanded={isOpen}
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="uppercase tracking-wide">Evidence</span>
          <span className="rounded-full border-2 border-gray-900 bg-primary-orange px-2 py-0.5 text-gray-900 dark:border-gray-500 dark:bg-primary-orange-dark dark:text-white">
            {referencedEpisodes.length} episode{referencedEpisodes.length === 1 ? "" : "s"}
          </span>
          <span className="rounded-full border-2 border-gray-900 bg-gray-100 px-2 py-0.5 text-gray-800 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100">
            {sourceCount} source{sourceCount === 1 ? "" : "s"}
          </span>
          {latestPerformance && (
            <span className="rounded-full border-2 border-gray-900 bg-gray-100 px-2 py-0.5 text-gray-800 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100">
              latest {formatLatency(latestPerformance.latency_ms)}
            </span>
          )}
          {latestTemporalScope && (
            <span className="rounded-full border-2 border-gray-900 bg-blue-100 px-2 py-0.5 text-blue-950 dark:border-blue-500 dark:bg-blue-900/40 dark:text-blue-100">
              {formatTemporalMode(latestTemporalScope)}
            </span>
          )}
        </span>
        <span aria-hidden="true" className="text-lg leading-none">
          {isOpen ? "−" : "+"}
        </span>
      </button>

      {isOpen && (
        <div className="mt-3 space-y-4 rounded-xl border-3 border-gray-900 bg-gray-50 p-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900/70 dark:text-gray-100">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border-2 border-gray-900 bg-white p-3 dark:border-gray-600 dark:bg-gray-800">
              <div className="text-[11px] font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Query
              </div>
              <div className="mt-1 font-semibold">
                {queryKinds.length ? queryKinds.join(", ") : "chat"}
              </div>
              {metadataEntries.at(-1)?.query.retrieval_budget && (
                <div className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {metadataEntries.at(-1)?.query.retrieval_budget} retrieval
                </div>
              )}
            </div>
            <div className="rounded-lg border-2 border-gray-900 bg-white p-3 dark:border-gray-600 dark:bg-gray-800">
              <div className="text-[11px] font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Scope
              </div>
              <div className="mt-1 font-semibold">
                {latestTemporalScope ? formatTemporalMode(latestTemporalScope) : "Current context"}
              </div>
              {latestTemporalScope && (
                <div className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {formatTemporalDetail(latestTemporalScope)}
                </div>
              )}
            </div>
            <div className="rounded-lg border-2 border-gray-900 bg-white p-3 dark:border-gray-600 dark:bg-gray-800">
              <div className="text-[11px] font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Evidence
              </div>
              <div className="mt-1 font-semibold">
                {diagnostics.evidenceRoleSummary || `${sourceCount} source${sourceCount === 1 ? "" : "s"}`}
              </div>
              <div className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                {formatSupportCounts(diagnostics.selectedSupport, diagnostics.candidateSupport)}
              </div>
            </div>
            <div className="rounded-lg border-2 border-gray-900 bg-white p-3 dark:border-gray-600 dark:bg-gray-800">
              <div className="text-[11px] font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Context
              </div>
              <div className="mt-1 font-semibold">
                {diagnostics.contextTokens.toLocaleString()} tokens
              </div>
            </div>
          </div>

          {latestTemporalScope && (
            <div className="rounded-lg border-2 border-blue-700 bg-blue-50 p-3 text-blue-950 dark:border-blue-500 dark:bg-blue-950/30 dark:text-blue-100">
              <div className="text-[11px] font-black uppercase tracking-wide">
                Time-aware scope
              </div>
              <div className="mt-1 text-sm font-semibold">
                {formatTemporalMode(latestTemporalScope)}
              </div>
              <div className="mt-1 text-xs font-medium">
                {formatTemporalDetail(latestTemporalScope)}
                {latestTemporalScope.reason ? ` · ${compactLabel(latestTemporalScope.reason)}` : ""}
              </div>
              {latestTemporalScope.resolved_episode_numbers_preview.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {latestTemporalScope.resolved_episode_numbers_preview.map((episodeNumber) => (
                    <span
                      key={episodeNumber}
                      className="rounded-full border border-blue-700 bg-white px-2 py-0.5 text-[11px] font-black text-blue-950 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-100"
                    >
                      #{episodeNumber}
                    </span>
                  ))}
                </div>
              )}
              {latestTemporalScope.llm_risk_flags?.length ? (
                <div className="mt-2 text-xs">
                  Risk flags: {latestTemporalScope.llm_risk_flags.map(compactLabel).join(", ")}
                </div>
              ) : null}
            </div>
          )}

          {diagnostics.reasons.length > 0 && (
            <div className="rounded-lg border-2 border-amber-600 bg-amber-50 p-3 text-amber-950 dark:bg-amber-900/30 dark:text-amber-100">
              <div className="text-[11px] font-black uppercase tracking-wide">
                Diagnostic
              </div>
              <div className="mt-1">{diagnostics.reasons.join(", ")}</div>
              {diagnostics.relationClaimsSuppressed > 0 && (
                <div className="mt-1 text-xs">
                  Suppressed {diagnostics.relationClaimsSuppressed} weak relation claim
                  {diagnostics.relationClaimsSuppressed === 1 ? "" : "s"}.
                </div>
              )}
            </div>
          )}

          {referencedEpisodes.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Referenced episodes
              </div>
              <div className="grid gap-2">
                {referencedEpisodes.map((episode) => {
                  const localEpisode = episodesByNumber.get(episode.episode_number);
                  const title = episode.title ?? localEpisode?.title ?? `Episode ${episode.episode_number}`;
                  const publishedDate = formatDate(episode.published_date ?? localEpisode?.publishedDate);

                  return (
                    <button
                      type="button"
                      key={`${episode.episode_number}-${title}`}
                      onClick={() => localEpisode && onOpenEpisode(localEpisode)}
                      disabled={!localEpisode}
                      className={cn(
                        "rounded-lg border-2 border-gray-900 bg-white p-3 text-left transition-transform dark:border-gray-600 dark:bg-gray-800",
                        localEpisode ? "hover:-translate-y-0.5 hover:bg-gray-50 dark:hover:bg-gray-700" : "cursor-default",
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="text-xs font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Episode #{episode.episode_number}
                            {publishedDate ? ` · ${publishedDate}` : ""}
                          </div>
                          <div className="mt-1 font-bold leading-snug">{title}</div>
                        </div>
                        {episode.chunks_used > 0 && (
                          <span className="rounded-full border-2 border-gray-900 bg-gray-100 px-2 py-0.5 text-xs font-bold dark:border-gray-500 dark:bg-gray-700">
                            {episode.chunks_used} chunk{episode.chunks_used === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      {roleEntries(episode.evidence_roles).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {roleEntries(episode.evidence_roles).slice(0, 4).map(([role, count]) => (
                            <span
                              key={role}
                              className={cn("rounded-full border px-2 py-0.5 text-[11px] font-bold", roleTone(role))}
                            >
                              {compactLabel(role)} {count}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {sources.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Source spans
              </div>
              <div className="space-y-2">
                {sources.map((source) => {
                  const timeRange = formatTimeRange(source);
                  const charRange = formatCharRange(source);
                  const score = formatScore(source.score);
                  const publishedDate = formatDate(source.published_date);
                  const sourceTitle =
                    source.title ??
                    (source.episode_number ? episodesByNumber.get(source.episode_number)?.title : null) ??
                    titleCaseLabel(source.content_origin);

                  return (
                    <div
                      key={source.source_id}
                      className="rounded-lg border-2 border-gray-900 bg-white p-3 dark:border-gray-600 dark:bg-gray-800"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-300">
                        {source.episode_number && <span>#{source.episode_number}</span>}
                        {publishedDate && <span>{publishedDate}</span>}
                        {timeRange && <span>{timeRange}</span>}
                        {!timeRange && charRange && <span>chars {charRange}</span>}
                        {typeof source.chunk_index === "number" && <span>chunk {source.chunk_index}</span>}
                        {(source.speaker_name || source.speaker_label) && (
                          <span>{source.speaker_name ?? source.speaker_label}</span>
                        )}
                        {score && <span>score {score}</span>}
                      </div>
                      <div className="mt-1 font-semibold leading-snug">{sourceTitle}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-bold", roleTone(source.evidence_role))}>
                          {compactLabel(source.evidence_role)}
                        </span>
                        <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-bold", originTone(source.content_origin))}>
                          {compactLabel(source.content_origin)}
                        </span>
                        {source.retrieval_source && (
                          <span className="rounded-full border border-gray-300 px-2 py-0.5 text-[11px] font-bold text-gray-600 dark:border-gray-600 dark:text-gray-300">
                            {compactLabel(source.retrieval_source)}
                          </span>
                        )}
                      </div>
                      {source.text_preview && (
                        <p className="mt-2 border-l-4 border-gray-300 pl-3 text-sm leading-relaxed text-gray-700 dark:border-gray-600 dark:text-gray-200">
                          {source.text_preview}
                        </p>
                      )}
                      {source.selection_reason && (
                        <p className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                          {compactLabel(source.selection_reason)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {allSources.length > sources.length && (
                <p className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Showing {sources.length} of {allSources.length} sources.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function aggregateReferencedEpisodes(
  metadataEntries: AskResponseMetadata[],
  fallbackEpisodeNumbers?: number[],
): AskReferencedEpisodeMetadata[] {
  const byNumber = new Map<number, AskReferencedEpisodeMetadata>();

  for (const metadata of metadataEntries) {
    for (const episode of metadata.referenced_episodes) {
      const existing = byNumber.get(episode.episode_number);
      if (!existing) {
        byNumber.set(episode.episode_number, {
          ...episode,
          evidence_roles: { ...episode.evidence_roles },
        });
        continue;
      }

      existing.title ||= episode.title;
      existing.title_key ||= episode.title_key;
      existing.published_date ||= episode.published_date;
      existing.chunks_used += episode.chunks_used;
      for (const [role, count] of Object.entries(episode.evidence_roles)) {
        existing.evidence_roles[role] = (existing.evidence_roles[role] ?? 0) + count;
      }
    }
  }

  for (const episodeNumber of fallbackEpisodeNumbers ?? []) {
    if (byNumber.has(episodeNumber)) continue;
    byNumber.set(episodeNumber, {
      episode_number: episodeNumber,
      title: null,
      title_key: null,
      published_date: null,
      chunks_used: 0,
      evidence_roles: {},
    });
  }

  return Array.from(byNumber.values()).sort((left, right) => {
    const leftPublished = new Date(left.published_date ?? 0).getTime();
    const rightPublished = new Date(right.published_date ?? 0).getTime();

    if (rightPublished !== leftPublished) {
      return rightPublished - leftPublished;
    }

    return left.episode_number - right.episode_number;
  });
}

function aggregateDiagnostics(metadataEntries: AskResponseMetadata[]) {
  const reasons = Array.from(
    new Set(
      metadataEntries
        .map((entry) => compactLabel(entry.answer_diagnostics.reason))
        .filter((reason) => reason && reason !== "answer"),
    ),
  );

  return {
    reasons,
    evidenceRoleSummary: formatEvidenceRoleSummary(metadataEntries),
    selectedSupport: metadataEntries.reduce(
      (sum, entry) => sum + entry.answer_diagnostics.selected_support_count,
      0,
    ),
    candidateSupport: metadataEntries.reduce(
      (sum, entry) => sum + entry.answer_diagnostics.candidate_support_count,
      0,
    ),
    contextTokens: metadataEntries.reduce(
      (sum, entry) => sum + entry.performance.context_tokens_est,
      0,
    ),
    relationClaimsSuppressed: metadataEntries.reduce(
      (sum, entry) =>
        sum + (entry.answer_diagnostics.relation_claims_suppressed ? 1 : 0),
      0,
    ),
  };
}

function formatEvidenceRoleSummary(metadataEntries: AskResponseMetadata[]): string {
  const roleCounts = new Map<string, number>();

  for (const entry of metadataEntries) {
    for (const [role, count] of Object.entries(entry.answer_diagnostics.context_evidence_roles)) {
      roleCounts.set(role, (roleCounts.get(role) ?? 0) + count);
    }
  }

  return Array.from(roleCounts.entries())
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0].localeCompare(right[0]);
    })
    .slice(0, 3)
    .map(([role, count]) => `${compactLabel(role)} ${count}`)
    .join(" · ");
}
